<template>
  <div v-if="type == 'devices'" id="device-search-table">
    <v-row>
      <v-col cols="3">
          <div class="devices-search-bar">
              <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Refine"
                  single-line
                  hide-details
              ></v-text-field>
          </div>
      </v-col>
      <div style="margin-left: 30px">
        <v-chip-group v-model="selectedFilter" multiple active-class="third--text" class="ma-4">
          <v-chip v-for="tag in filters" :key="tag">{{ tag.text }}</v-chip>
        </v-chip-group>
      </div>
      <v-spacer/>
      <v-col cols="1" class="pt-6 pl-12">
        <v-icon v-if="!excelLoading" :disabled="loadingPresence" :loading="loadingPresence" @click="exportExcel">
          mdi-file
        </v-icon>
        <v-icon v-else class="updating">
          mdi-refresh
        </v-icon>
      </v-col>
    </v-row>

    <v-data-table
			:headers="headers"
			:items="filteredSet"
			:loading="loading || loadingPresence"
			:search="search"
			:sort-desc="true"
      class="elevation-1 admin-table click-row"
      :items-per-page="100"
      hide-default-footer
		>
      <template v-slot:item.status="{ item }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-avatar
							:color="mapStatusColor(item)"
							size="20"
							v-bind="attrs"
							v-on="on"
						>
						</v-avatar>
					</template>
					<span>{{ getCurrentStatus(item) }}</span>
				</v-tooltip>
			</template>

			<template v-slot:item.last_seen="{ item }">
				{{ getLastSeen(item) }}
			</template>

      <template v-slot:item.device_status="{ item }">
        {{ deviceStateMap(item)  }}
      </template>

      <template v-slot:item.disabled="{ item }">
        {{ deviceEnabledMap(item)  }}
      </template>

      <template v-slot:item.is_default="{ item }">
        {{ deviceClassMap(item)  }}
      </template>

      <template v-slot:item.actions="{item}">
        <div v-if="item.device_status == 'assigned'"  class="reports-action-launch" @click="launchRowAction">
          <v-icon>mdi-dots-vertical</v-icon>
          <ActionContainer :ref="item.device_id" :heading="'Action'" class="report-action-options">
            <ActionItem 
              :disabled="!$can('unassignDeviceFromVehicle')"
              label="Unassign"
              icon="mdi-link"
              @click.native="setUnassignTarget(item)"
            />
            <ActionItem
              :disabled="!$can('viewDeviceInfo')"
              label="Device Config"
              icon="mdi-settings"
              @click.native="toggleDeviceConfig(item)"
            />
            <ActionItem 
              label="Retrieve Logs"
              icon="mdi-file"
              @click.native="toggleLogComp(item)"/>
            <ActionItem 
              :disabled="!$can('updateVehicle')"
              label="Edit Vehicle"
              icon="mdi-pencil"
              @click.native="toggleConfigComponent(item, 'vehicle')"/>

            <ActionItem 
              :disabled="!$can('viewDeviceInfo')"
              icon="mdi-laptop"
              label="Device Info"
              @click.native="toggleConfigComponent(item, 'device')"/>

            <ActionItem 
              :disabled="!$can('editAlertConfig')"
              icon="mdi-bell"
              label="Alert Config"
              @click.native="toggleConfigComponent(item, 'alert')"/>
            <ActionItem 
              label="Format Disk"
              icon="mdi-harddisk"
              @click.native="toggleFormat(item)"
            />
            <ActionItem 
              label="Reboot Device"
              icon="mdi-power"
              @click.native="toggleReboot(item)"/>
            <ActionItem
              :disabled="!$can('createFirmwareUpdateJob') && !$can('createDeviceConfigJob') && !$can('createDeviceInstallJob')"
              label="Create Job"
              icon="mdi-clipboard-list"
              @click.native="toggleJobCreation(item)"/>

            <ActionItem 
              :disabled="!item.vehicle_id || item.vehicle_id == ''"
							label="Move to Org"
							icon="mdi-arrow-right"
							@click.native="toggleMoveComp(item)"/>
            
            <ShowOnMap :org="item.org_id" :vehicle="item.vehicle_id" />
          </ActionContainer>

          <ActionContainer v-if="item.device_status == 'unassigned'" :ref="item.device_id" :heading="'Action'" class="report-action-options">
            <ActionItem
              :disabled="!$can('assignDeviceToVehicle')"
              label="Assign"
              icon="mdi-link"
              @click.native="toggleAssignmentComp(item)"
            />
            <ActionItem
              :disabled="!$can('viewDeviceInfo')"
              label="Device Config"
              icon="mdi-settings"
              @click.native="toggleDeviceConfig(item)"
            />
            <ActionItem 
              label="Format Disk"
              icon="mdi-harddisk"
              @click.native="toggleFormat(item)"
            />
            <ActionItem 
              label="Reboot Device"
              icon="mdi-power"
              @click.native="toggleReboot(item)"/>
          </ActionContainer>
        </div>

        <div v-if="item.device_status == 'unassigned'"  class="reports-action-launch" @click="launchRowAction">
          <v-icon>mdi-dots-vertical</v-icon>
          <ActionContainer :ref="item.device_id" :heading="'Action'" class="report-action-options">
            <ActionItem
              :disabled="!$can('assignDeviceToVehicle')"
              label="Assign"
              icon="mdi-link"
              @click.native="toggleAssignmentComp(item)"
            />
            <ActionItem
              :disabled="!$can('viewDeviceInfo')"
              label="Device Config"
              icon="mdi-settings"
              @click.native="toggleDeviceConfig(item)"
            />
            <ActionItem 
              :disabled="!$can('deleteDevice')"
              label="Delete"
              icon="mdi-delete"
              @click.native="setDeleteDeviceTarget(item)"/>
            <ActionItem 
              label="Format Disk"
              icon="mdi-harddisk"
              @click.native="toggleFormat(item)"
            />
            <ActionItem 
              label="Reboot Device"
              icon="mdi-power"
              @click.native="toggleReboot(item)"/>
          </ActionContainer>
        </div>

        <div v-if="item.device_status == 'pending'"  class="reports-action-launch" @click="launchRowAction">
          <v-icon>mdi-dots-vertical</v-icon>
          <ActionContainer :ref="item.device_id" :heading="'Action'" class="report-action-options">
            <ActionItem 
              label="Update OTC"
              icon="mdi-link"
              @click.native="setUpdateOTCTarget(item)"/>
            <ActionItem 
              :disabled="!$can('deleteDevice')"
              label="Delete"
              icon="mdi-delete"
              @click.native="setDeleteDeviceTarget(item)"/>
          </ActionContainer>
        </div>
      </template>
    </v-data-table>
    
    <Pagination
      :pagination-info="paginationInfo"
      @updatedPage="fetchNewPage"
    />

    <DeviceConfig 
			v-if="displayDeviceConfig"
      :key="compKey"
			:item="selectedDevice"
			:org="selectedDeviceOrgId"
      :device-id="selectedDevice.device_id"
      @closeDeviceConfig="displayDeviceConfig = !displayDeviceConfig"
      @forceUpdateConfig="handleForceUpdate"
    />

    <Prompt
      :display="displayUnassignPrompt"
      :close-target="'closeUnassign'"
      :submit-target="'submitUnassign'"
      :title="'Unassign Device'"
      :message="'Are you sure you wish to unassign this device?'" 
      :state="unassignState"
      :res="unassignRes"
      @closeUnassign="toggleUnassignPrompt"
      @submitUnassign="unassignDevice"
    />

    <DownloadLog 
			:display="showLogs"
			:device="selectedDevice" 
			:org="selectedDeviceOrgId"
			@closeLogComp="closeLogComp"
		/>

    <InfoContainer
      :display="displayVehicleInfo"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="displayVehicleInfo = false"
    >
      <pvcam-wc-vehicle-config 
        :items="JSON.stringify([selectedVehicle])"
        :groups="JSON.stringify(selectedVehicleGroup)"
        :org="JSON.stringify(selectedDeviceOrgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify([])"
        :multi-device="JSON.stringify(false)"
        :admin="true"
        :translate="JSON.stringify(false)"
      />
    </InfoContainer>

		<!-- <InfoContainer
      :display="displayDeviceInfo"
      :width="1000"
      :panel-heading="panelHeading"
      @closeModal="displayDeviceInfo = false"
    >
      <pvcam-wc-device-info
        v-if="vehicleIsLoaded"
        :org="JSON.stringify(selectedDeviceOrgId)"
        :items="JSON.stringify([selectedVehicle])"
      />
    </InfoContainer> -->

    <DeviceInfo 
			v-if="displayDeviceInfo"
      :display-device-info="displayDeviceInfo"
      :device-id="selectedVehicleDeviceId"
			:vehicle="selectedVehicle"
      :org="selectedDeviceOrgId"
      @close="displayDeviceInfo = false"
    />

		<InfoContainer
      :display="displayAlertConfig"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="displayAlertConfig = false"
    >
      <pvcam-wc-device-alert-config
        :items="JSON.stringify([selectedVehicle])"
        :groups="JSON.stringify([])"
        :org="JSON.stringify(selectedDeviceOrgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify([])"
        :multi-config="JSON.stringify(false)"
        :selected-contacts="JSON.stringify([])"
        :user-info="JSON.stringify({})"
				:admin-user="true"
      />
    </InfoContainer>

    <DeviceFormat 
			v-if="displayFormat"
			:device="formatItem"
			:org="selectedDeviceOrgId"
			@close="toggleFormat(null)"
    />

    <DeviceReboot 
			v-if="displayReboot"
			:org="selectedDeviceOrgId"
			:device="rebootDevice"
			@close="toggleReboot(null)"
		/>

    <add-job 
      :key="refreshAddJob"
			:open="jobVisibility" 
			:mode="'new'" 
			:job-item="jobItem"
			@exit="toggleJobCreation(null)"
		/>

    <DeviceAssignment
      v-if="showDeviceAssignment"
      :orgs="orgs"
      :selected-devices="[selectedDevice]"
      @closeDeviceAssignment="toggleAssignmentComp(null)"
      @closeAllAssignmentWindows="toggleAssignmentComp(null)"
    />

    <Prompt
      :display="displayDeleteDevice"
      :close-target="'closeDeleteDevice'"
      :submit-target="'submitDeleteDevice'"
      :title="'Delete Device'"
      :message="'Are you sure you wish to delete this device?'"
      :state="deleteState" 
      :res="deleteRes"
      @closeDeleteDevice="toggleDeleteDevicePrompt"
      @submitDeleteDevice="deleteDevice"
    />

    <Prompt
      :display="displayUpdateOTC"
      :close-target="'closeUpdateOTC'"
      :submit-target="'submitUpdateOTC'"
      :title="'Update OTC'"
      :message="'Are you sure you wish to update the OTC?'" 
      :state="updateState"
      :res="updateRes"
      @closeUpdateOTC="toggleUpdateOTC"
      @submitUpdateOTC="regenOTC"
    />

    <MoveVehicleBetweenOrg
			v-if="displayMoveVehicle"
			:orgs="orgs"
			:vehicle-list="itemsForMoving"
			@close="toggleMoveComp(null)"
		/>
  </div>
</template>

<script>

import {
		GetOrgVehiclePresence,
    UnassignDeviceFromVehicle,
    GetOrgVehicleById,
    DeletePendingDevice,
    DeleteUnassignedDevice,
    RegenerateOTC
	} from "@/services/API.js";

import moment from "moment";
import * as xlsx from 'xlsx'
import { mapState, mapMutations, mapActions } from "vuex";
import { search } from '@/mixins/quickSearch.js'
import { getColor } from '@/utils'

import { AmplifyEventBus } from 'aws-amplify-vue'

import Pagination from './Pagination.vue';
import ActionContainer from '@/components/general/ActionContainer.vue';
import ActionItem from '@/components/general/ActionItem.vue';
import Prompt from '@/components/general/Prompt.vue';
import DeviceConfig from '@/components/deviceConfig/config.vue';
import DownloadLog from '@/views/OrgVehicles/components/DownloadLog.vue';
import InfoContainer from '@/components/general/InfoContainer.vue';
import DeviceFormat from '@/components/general/DeviceFormat.vue';
import DeviceReboot from '@/components/general/DeviceReboot.vue';
import AddJob from "@/views/Jobs/Sections/AddJob.vue";
import DeviceAssignment from '@/views/DeviceManagement/components/DeviceAssignment.vue';
import DeviceInfo from '@/components/deviceInfo/deviceInfo.vue';
import MoveVehicleBetweenOrg from '@/components/general/MoveVehicleBetweenOrg.vue'
import ShowOnMap from '@/components/general/menu/ShowOnMap.vue'

import { Logger } from "aws-amplify";
const logger = new Logger("Device Search");

export default {
  name: 'Devices',
  components: {
    Pagination,
    ActionContainer,
    ActionItem,
    Prompt,
    DeviceConfig,
    DownloadLog,
    InfoContainer,
    DeviceFormat,
    DeviceReboot,
    AddJob,
    DeviceAssignment,
    DeviceInfo,
    MoveVehicleBetweenOrg,
    ShowOnMap
  },
  mixins: [ search ],
  props: {
    update: {
      type: Number,
      default: 0
    },
    type: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        pageSize: 100
      },
      search: '',
      loading: false,
      loadingPresence: false,
      headers: [
				{
          text: 'Status',
          value: 'status',
          align: 'left'
        },
				{
					text: 'Last Seen',
					value: 'last_seen',
					align: 'left'
				},
        
        {
					text: 'Device State',
          value: 'device_status',
          align: 'left'
				},
				{
					text: 'Install Tag',
					value: 'install_tag',
					align: 'left'
				},
        {
          text: 'OTC',
          value: 'otc',
          align: 'left'
        },
        {
          text: 'IMEI',
          value: 'imei',
          align: 'left'
        },
        {
          text: 'Model',
          value: 'model',
          align: 'left'
        },
        {
          text: 'Firmware',
          value: 'firmware_version',
          align: 'left'
        },
        {
          text: 'Enabled',
          value: 'disabled',
          align: 'left'
        },
        {
          text: 'Device Class',
          value: 'is_default',
          align: 'left'
        },
        {
					text: 'Organisation',
          value: 'org_name',
          align: 'left'
				},
        {
					text: 'Vehicle',
          value: 'vehicle_name',
          align: 'left'
				},
        {
          text: 'Actions',
          value: 'actions',
          align: 'left'
        }
      ],
      tableData: [],
      orgIds: [],
      presenceData: [],
      orgVehicle: {},
      
      unassignTarget: {},
      displayUnassignPrompt: false,
      unassignState: false,
      unassignRes: null,

      selectedDevice: {},
      selectedVehicle: {},
      displayDeviceConfig: false,
      compKey: 0,

      showLogs: false,

      displayVehicleInfo: false,
      displayDeviceInfo: false,
      displayAlertConfig: false,

      displayFormat: false,
      formatItem: {},

      rebootDevice: {},
      displayReboot: false,

      jobVisibility: false,
      jobItem: null,
      refreshAddJob: 0,

      showDeviceAssignment: false,

      deleteState: false,
      deleteRes: null,
      displayDeleteDevice: false,

      updateRes: null,
      displayUpdateOTC: false,
      updateState: false,

      selectedFilter: [],
      filters: [
        { text: 'Assigned', val: 'assigned'},
        { text: 'Unassigned', val: 'unassigned'},
        { text: 'Pending', val: 'pending'},
        { text: 'Enabled', val: 'False'},
        { text: 'Disabled', val: 'True'}
      ],
      excelLoading: false,
      displayMoveVehicle: false,
      itemsForMoving: []
    }
  },
  computed: {
    ...mapState([
      'quickSearchResults',
      'quickSearchPayload',
      'orgs'
    ]),
    selectedDeviceOrgId() {
      return this.selectedDevice.hasOwnProperty('org_id') > 0 ? this.selectedDevice.org_id : '';
    },
    selectedVehicleOrg() {
      return this.selectedDevice.hasOwnProperty('org_id') > 0 ? this.selectedDevice.org_id : '';
    },
    selectedVehicleGroup() {
      return this.selectedVehicle.hasOwnProperty('node_name') ? [this.selectedVehicle.node_name] : '';
    },
    hasData() {
      const data = this.quickSearchResults;

      return Object.keys(data).length > 0;
    },
    currentPage() {
      return this.hasData ? this.quickSearchPayload.pageNum : 1;
    },
    currentRoute() {
      return this.$route.path;
    },
    hasPayload() {
      const data = this.quickSearchPayload;

      return Object.keys(data).length > 0;
    },
    panelHeading() {
      return this.selectedVehicle.hasOwnProperty('vehicle_name') ? this.selectedVehicle.vehicle_name : '';
    },
    vehiType() {
      return this.selectedVehicle.hasOwnProperty('vehicle_type') ? [this.selectedVehicle.vehicle_type] : '';
    },
    filteredSet () {
      if(!this.selectedFilter.length) {
        return this.tableData
      } else {
        const filters = this.selectedFilter.map(i => this.filters[i].val)

        return this.tableData.filter(i => filters.includes(i.device_status) || filters.includes(i.disabled))
      }
    },
    vehicleIsLoaded() {
      return Object.keys(this.selectedVehicle).length > 0 ? true : false;
    },
    selectedVehicleDeviceId() {
      const vehicle = this.selectedVehicle ? this.selectedVehicle : null
      const devices = Object.hasOwn(vehicle, 'devices') && vehicle.devices.length > 0 ? vehicle.devices : []
      const defaultDevice = devices.length > 0 ? devices.find(device => device.is_defaultdevice == true) : null
      const deviceId = defaultDevice ? defaultDevice.device_id : ''

      return deviceId
    }
  },
  watch: {
    quickSearchResults: {
      handler() {
        logger.info('quick search res')
        this.compileTableData();
        this.buildPaginationInfo();
      },
      deep: true
    }
  },
  mounted() {
    this.getOrgs();
    this.compileTableData();
    this.buildPaginationInfo();

    window.$translate = (section, value) => {
      logger.info('translate', section, value)
    }
  },
  methods: {
    ...mapActions(["getOrgs"]),
    ...mapMutations("websockets", ["manualRefresh"]),
    toggleMoveComp(item) {
      this.displayMoveVehicle = !this.displayMoveVehicle

      const itemToBeMoved = {
        id: item.vehicle_id
      }

      if(item) {
        if(!this.itemsForMoving.includes(itemToBeMoved)) {
          this.itemsForMoving.push(itemToBeMoved);
        }
      } else {
        this.selectedItems = []
      }
    },
    
    async exportExcel() {
      this.excelLoading = true
      const exportSheetName = `Devices-Global-Search-${moment().format('YYYY-MM-DDTHH:mm:ss')}`

      const headers = [
				'Status',
				'Last Seen',
        'Device State',
        'Install Tag',
        'OTC',
        'IMEI',
        'Model',
        'Firmware',
        'Enabled',
        'Device Class',
        'Organisation',
        'Vehicle'
      ]
      const payloadCopy = JSON.parse(JSON.stringify(this.quickSearchPayload))
      const fullSearch = await this.fullSearch(payloadCopy, this.paginationInfo)

      // we need to rebuild the org id list from the full results
      this.orgIds = []

      fullSearch.forEach(device => {
        let id = device.org_id ? device.org_id : null
        if (id && !this.orgIds.includes(id)) {
          this.orgIds.push(id)
        }  
      })

      await this.getPresenceData()

      const devices = fullSearch.map(item => (
        { 
          ...item, 
          status: this.getCurrentStatus(item),
          lastSeen: this.getLastSeen(item),
          deviceState: this.deviceStateMap(item),
          enabled: this.deviceEnabledMap(item),
          deviceClass: this.deviceClassMap(item)
        }
      ))

      const data = [
        headers,
        [],
        ...devices.map(item => [
          item.status ? item.status : '',
          item.lastSeen,
          item.deviceState,
          Object.prototype.hasOwnProperty.call(item, 'install_tag') ? item.install_tag : '',
          Object.prototype.hasOwnProperty.call(item, 'otc') ? item.otc : '',
          Object.prototype.hasOwnProperty.call(item, 'imei') ? item.imei : '',
          Object.prototype.hasOwnProperty.call(item, 'model') ? item.model : '',
          Object.prototype.hasOwnProperty.call(item, 'firmware_version') ? item.firmware_version : '',
          item.enabled,
          item.deviceClass,
          Object.prototype.hasOwnProperty.call(item, 'org_name') ? item.org_name : '',
          Object.prototype.hasOwnProperty.call(item, 'vehicle_name') ? item.vehicle_name : '',
        ])
      ]

      // Create a new workbook
      const wb = xlsx.utils.book_new()

      const ws = xlsx.utils.aoa_to_sheet(data)

        const colWidths = data[0].map((_, colIndex) => {
          const maxLength = data.reduce((max, row) => {
            const cellValue = row && row[colIndex] ? row[colIndex].toString() : ''
            return Math.max(max, cellValue.length)
          }, 0)
          return { width: maxLength + 2 }; // Add a little extra padding
        })

        // Set column widths in the worksheet
        ws['!cols'] = colWidths

        // Append the worksheet to the workbook
        xlsx.utils.book_append_sheet(wb, ws, 'Devices')

        // Write the workbook to a file (e.g., 'example.xlsx')
        xlsx.writeFile(wb, exportSheetName + ".xlsx")
        this.excelLoading = false
    },
    async getOrgVehicleById(orgId, vehicleId) {
      const vehicle = await GetOrgVehicleById(orgId, vehicleId, true);

      if (vehicle) {
        if(vehicle.devices.length > 0) {
          vehicle.devices.forEach(device => {
            device.is_default_device = true;
          });
        }
        logger.info('updated vehicle devices ', vehicle)
        return vehicle
      } else {
        return false;
      }
    },

    setUnassignTarget(item) {
      this.unassignRes = null;
      this.unassignTarget = item;
      this.toggleUnassignPrompt();
    },
    toggleUnassignPrompt() {
      this.displayUnassignPrompt = !this.displayUnassignPrompt;
    },
    async unassignDevice() {
      this.unassignState = false;

      let vehicleId = this.unassignTarget.vehicle_id;
      let deviceId = this.unassignTarget.device_id;
      let orgId = this.unassignTarget.org_id;
      
      logger.info('unassign device : ', orgId, vehicleId, deviceId);

      const unasign = await UnassignDeviceFromVehicle(orgId, vehicleId, deviceId);

      if (unasign) {
        this.unassignRes = {
          msg: 'Device Unassigned Successfully',
          code: 'green'
        }
      } else {
        this.unassignRes = {
          msg: 'Device Unassignment Failed',
          code: 'red'
        }
      }
      this.unassignState = true;
      this.unassignTarget = {};
    },

    toggleDeviceConfig(item) {
      logger.info('toggleDeviceConfig called', item)
      this.selectedDevice = item;
      this.displayDeviceConfig = !this.displayDeviceConfig;
    },
    handleForceUpdate() {
      this.compKey++;
    },

    toggleLogComp(item) {
      this.showLogs = !this.showLogs;
      this.selectedDevice = item;
    },
    closeLogComp() {
      this.showLogs = false;
    },

    async toggleConfigComponent(item, display) {
      logger.info(item, display)
      this.selectedDevice = item;
      const vehicle = await this.getOrgVehicleById(item.org_id, item.vehicle_id)
      logger.info('vehicle here ', vehicle)
      if (vehicle) {
        this.selectedVehicle = vehicle;
        this.selectedVehicle.group = vehicle.node_name;
      }

      switch (display) {
        case 'vehicle': {
          this.displayVehicleInfo = !this.displayVehicleInfo;

          break;
        }
        case 'device': {
          this.displayDeviceInfo = !this.displayDeviceInfo;

          break;
        }
        case 'alert': {
          this.displayAlertConfig = !this.displayAlertConfig;

          break;
        }
      }
    },

    async toggleFormat(item) {
      let vehicle = false;
      if(item) {
        vehicle = await this.getOrgVehicleById(item.org_id, item.vehicle_id);
        this.selectedDevice = item;
      }
      
      if (vehicle) {
        let devices = [{
          is_default_device: true,
          device_id: item.device_id
        }];

        vehicle.devices = devices;
        this.formatItem = vehicle;
      }

      this.displayFormat = !this.displayFormat;
    },

    async toggleReboot(item) {
      let vehicle = false;
      if(item) {
        vehicle = await this.getOrgVehicleById(item.org_id, item.vehicle_id);
        this.selectedDevice = item;
      }
      
      if (vehicle) {
        let devices = [{
          is_default_device: true,
          device_id: item.device_id
        }];

        vehicle.devices = devices;
        this.rebootDevice = vehicle;
      }

      this.displayReboot = !this.displayReboot;
    },

    async toggleJobCreation(item) {
      let vehicle = false;
      
      if(!item) {
        this.jobVisibility = !this.jobVisibility;

        return
      }
      if(item) {
        vehicle = await this.getOrgVehicleById(item.org_id, item.vehicle_id);
      }
      this.refreshAddJob++;
      this.jobVisibility = !this.jobVisibility;
      this.jobItem = vehicle;
    },

    toggleAssignmentComp(item) {
      this.showDeviceAssignment = !this.showDeviceAssignment;
      if(!item) return;
      this.selectedDevice = item;
    },

    setDeleteDeviceTarget(item) {
      this.deleteRes = null;
      this.selectedDevice = item;
      this.toggleDeleteDevicePrompt();
    },
    toggleDeleteDevicePrompt() {
      logger.info('toggleDeleteDevicePrompt ', this.displayDeleteDevice)
      this.displayDeleteDevice = !this.displayDeleteDevice;
    },
    async deleteDevice() {
      logger.debug("delete device called", this.selectedDevice)
      let id = null

      if(this.selectedDevice.hasOwnProperty('device_id')) {
        id = this.selectedDevice.device_id;
      } else if (this.selectedDevice.hasOwnProperty('physical_device_id')) {
        id = this.selectedDevice.physical_device_id;
      } else {
        this.deleteState = true;
        AmplifyEventBus.$emit('snackEvent', {
          color: 'error',
          text: 'Missing Physical ID'
        });
        
        return
      }

      this.deleteState = false;
      
      // we need to handle delete requests for unassigned devices seperately! 
      if(this.selectedDevice.device_status == 'unassigned') {
        const deleteItem = await DeleteUnassignedDevice(id);

         if (deleteItem) {
          this.deleteRes = {
            msg: 'Device Deleted Successfully',
            code: 'green'
          }
        } else {
          this.deleteRes = {
            msg: 'Device Deletion failed',
            code: 'red'
          }
        }
      } else {
        const deleteItem = await DeletePendingDevice(id);

        if (deleteItem) {
          this.deleteRes = {
            msg: 'Device Deleted Successfully',
            code: 'green'
          }
        } else {
          this.deleteRes = {
            msg: 'Device Deletion failed',
            code: 'red'
          }
        }
      }

      let filterKey = this.selectedDevice.hasOwnProperty('device_id') ? 'device_id' : 'physical_device_id'

      if(filterKey == 'device_id') {
        this.tableData = this.tableData.filter(item => item.device_id != id)
      }
      else {
        this.tableData = this.tableData.filter(item => item.physical_device_id != id)
      }

      this.deleteState = true;
    },

    setUpdateOTCTarget(item) {
      logger.info('setUpdateOTCTarget ', item)
      this.updateRes = null;
      this.selectedDevice = item;
      this.toggleUpdateOTC();
    },
    toggleUpdateOTC() { 
      this.displayUpdateOTC = !this.displayUpdateOTC;
    },
    async regenOTC() {
      let id = null;

      if(this.selectedDevice.hasOwnProperty('physical_device_id')) {
        id = this.selectedDevice.physical_device_id;
      } else {
        this.updateOTCTarget = {}
        this.updateState = true;
        AmplifyEventBus.$emit('snackEvent', {
          color: 'error',
          text: 'Missing Physical ID'
        });
        
        return
      }
      
      this.updateState = false;
      const update = await RegenerateOTC(id);

      if (update) {
        this.updateRes = {
          msg: `Updated OTC : ${update.otc}`,
          code: 'green'
        }

        let tableItem = this.tableData.findIndex(item => item.physical_device_id == id)
        
        this.tableData[tableItem].otc = update.otc
      } else {
        this.updateRes = {
          msg: 'OTC Regeneration failed',
          code: 'red'
        }
      }

      this.updateState = true;
      this.updateOTCTarget = {}
    },

    compileTableData() {
      const data = this.quickSearchResults;
      let results = []

      if(this.hasData) {
        if (data.res.Result.length > 0) {
          results = JSON.parse(JSON.stringify(data.res.Result))
        } else {
          results = [];
        }
        logger.info('res here ', results)
        this.tableData = results

        if (this.tableData.length > 0) {
          this.compileOrgIds();
        }
      }
      logger.info('table data',this.tableData)
    },
    async fetchNewPage(page) {
      if(!this.hasData) return
      if(page == this.currentPage) return

      this.loading = true;

      this.$store.commit('updateQuickSearchPage', page);

      const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

      if (search) {
        this.$store.commit('setQuickSearchPayload', this.quickSearchPayload);
        this.loading = false;
      }
      else {
        this.loading = false;
      }
      logger.info('handle page update ', page)
    },
    buildPaginationInfo() {
      logger.info('buildPaginationInfo')
      if(!this.quickSearchResults.hasOwnProperty('res')) return;

      const data = this.quickSearchResults.res;

      if(data.hasOwnProperty('page_number')) {
        this.paginationInfo.pageNum = data.page_number;
      }
      if(data.hasOwnProperty('total_pages')) {
        this.paginationInfo.pageCount = data.total_pages;
      }
      logger.info('has pagination info ', this.paginationInfo)
    },
    compileOrgIds() {
      logger.info('compileOrgIds called')
      this.orgIds = [];
    
      this.tableData.forEach(device => {
        let id = device.org_id ? device.org_id : null;

        if (id && !this.orgIds.includes(id)) {
          this.orgIds.push(id);
        }  
      });

      this.getPresenceData();
    },

    async getPresenceData() {
      const idList = this.orgIds
      this.loadingPresence = true;

      logger.debug("getPresenceData org list", idList)

      let collection = [];
      for (let i = 0; i < idList.length; i++) {
        collection.push(GetOrgVehiclePresence(this.orgIds[i], true).catch(err => logger.debug('could not fetch org', err)))
      }		
      
      const results = await Promise.all(collection);
      const combinedResults = [].concat.apply([], results);

      this.presenceData = combinedResults;
      this.loadingPresence = false;
      return true; // return so the await function works in the excel download
    },
    mapStatusColor(device) {
      const statusObj = this.presenceData.find(item => item.vehicle_id == device.vehicle_id)

      if(statusObj) {
        return this.statusColor(statusObj.status)
      }
    },
    getCurrentStatus(device) {
      const statusObj = this.presenceData.find(item => item.vehicle_id == device.vehicle_id)

      if(statusObj) {
        return statusObj.status;
      }
    },
    statusColor(status) {
      return getColor(status)
    },
    deviceStateMap(val) {
      if(!val.device_status) return ;
      const map = {
        assigned: 'Assigned',
        unassigned: 'Unassigned',
        unknown: 'Unknown',
        pending: 'Pending'
      }

      return map.hasOwnProperty(val.device_status) ? map[val.device_status] : '';
    },
    deviceClassMap(val) {
      if(!val.is_default) return 'Secondary';

      return val.is_default == 'True' || val.is_default == true ? 'Primary' : 'Secondary';
    },
    deviceEnabledMap(val) {
      if(!val.disabled) return 'YES';

      return val.disabled == 'True' || val.disabled == true ? 'NO' : 'YES';
    },
    getLastSeen(vehicle) {
      const statusObj = this.presenceData.find(item => item.vehicle_id == vehicle.vehicle_id)

      if(statusObj) {
        return moment(statusObj.last_seen_local_time).format('YYYY MMM DD HH:mm:ss');
      } else {
        return 'Unknown';
      }
    },
    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove('active-options');
        if (option === e.target.nextSibling) {
          option.classList.toggle('active-options');
        } 
      }
    }
  }
}
</script>

<style lang="scss">
  .devices-search-bar {
    padding-top: 1em;
    padding-left: 1em;
  }

  .reports-action-launch {
    position: relative;
    cursor: pointer;
  }
  .report-action-options {
    display: none;
  }
  .active-options {
    display: block !important;  
  }

  .v-data-table__wrapper {
    overflow-x: visible !important;
    overflow-y: visible !important;
  }
  .updating {
    animation-name: spin;
    animation-duration: 750ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear; 
  }

  @keyframes spin {
    from {
        transform:rotate(0deg);
    }
    to {
        transform:rotate(360deg);
    }
  }
</style>